/**
 * Waste Element Colors Store — user-customisable colors per element type
 *
 * @module core/modules/waste/store/element-colors-store
 *
 * @description
 * Zustand store with localStorage persistence. Keeps only user overrides;
 * falls back to WASTE_ELEMENT_COLORS defaults for untouched entries.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { WASTE_ELEMENT_COLORS, type WasteElementId } from '../constants/element-colors';

interface WasteElementColorsState {
  /** Only colours the user has explicitly changed */
  overrides: Partial<Record<WasteElementId, string>>;
}

interface WasteElementColorsActions {
  /** Return override if set, otherwise default */
  getColor: (id: WasteElementId) => string;
  /** Set a user override */
  setColor: (id: WasteElementId, hex: string) => void;
  /** Remove a single override (revert to default) */
  resetColor: (id: WasteElementId) => void;
  /** Remove all overrides */
  resetAll: () => void;
}

export type WasteElementColorsStore = WasteElementColorsState & WasteElementColorsActions;

export const useWasteElementColorsStore = create<WasteElementColorsStore>()(
  devtools(
    persist(
      (set, get) => ({
        overrides: {},

        getColor(id: WasteElementId): string {
          return get().overrides[id] ?? WASTE_ELEMENT_COLORS[id];
        },

        setColor(id: WasteElementId, hex: string): void {
          set(
            (s) => ({ overrides: { ...s.overrides, [id]: hex } }),
            false,
            'setColor',
          );
        },

        resetColor(id: WasteElementId): void {
          set(
            (s) => {
              const next = { ...s.overrides };
              delete next[id];
              return { overrides: next };
            },
            false,
            'resetColor',
          );
        },

        resetAll(): void {
          set({ overrides: {} }, false, 'resetAll');
        },
      }),
      { name: 'waste-element-colors' },
    ),
    { name: 'WasteElementColors' },
  ),
);
